import TextAbbreviationControl from 'modules/Abbreviations/components/ToolbarAbbreviation/TextAbbreviationControl';
import React, { useState } from 'react';
import { TextEditorSetters, TextEditorProps } from 'components/ArtboardAssets/Text/hooks/TextEditorHook';
import { CellProps } from 'components/ArtboardAssets/Text/utils/cell';
import { Styles, StylesSetters } from 'components/ArtboardAssets/Text/utils/styles';
import BaseToolbar from 'components/Toolbar/BaseToolbar';
import Subtitle from 'components/Toolbar/BaseToolbar/components/Subtitle';
import { HOVER_HINT } from 'components/Toolbar/constants';
import BackgroundImage from 'components/Toolbar/Controls/BackgroundImage';
import BorderControl from 'components/Toolbar/Controls/Border';
import BorderRadius from 'components/Toolbar/Controls/BorderRadius';
import FillColor from 'components/Toolbar/Controls/FillColor';
import FontColor from 'components/Toolbar/Controls/FontColor';
import FontSize from 'components/Toolbar/Controls/FontSize';
import HeadingTagControl from 'components/Toolbar/Controls/HeadingTag';
import Link from 'components/Toolbar/Controls/Link';
import Padding from 'components/Toolbar/Controls/Padding';
import TextLineHeight from 'components/Toolbar/Controls/TextLineHeight';
import TextVerticalAlignment from 'components/Toolbar/Controls/TextVerticalAlignment';
import Token from 'components/Toolbar/Controls/Token';
import AutoFitContentToggle from 'components/Toolbar/Shared/AutoFitContentToggle';
import ControlGroup from 'components/Toolbar/Shared/ControlGroup';
import HeightWidth from 'components/Toolbar/Shared/HeightWidth';
import * as Constants from 'const';
import * as Models from 'models';
import { intlGet } from 'utils/intlGet';
import { ToolbarDropdownType } from './constants';
import BrandStyleControl from './controls/BrandStyleControl';
import BulletedListControl from './controls/BulletedListControl';
import CharStyleControl from './controls/CharStyleControl';
import FontFamilyControl from './controls/FontFamilyControl';
import ScriptControl from './controls/ScriptControl';
import TextHorizontalAlignmentControls from './controls/TextHorizontalAlignmentControls';
import WrapControl from './controls/WrapControl';
import css from './styles.module.scss';

type Props = {
  layoutId: string;
  projectType: Constants.ProjectType;
  returnFocusToEditor: () => void;

  editorProps: TextEditorProps;
  editorSetters: TextEditorSetters;

  contentMinHeight: number;

  styles: Styles;
  stylesSetters: StylesSetters;

  cellProps: CellProps;
  toggleColumnWidth: (newWidth: number) => number;
  toggleCellHeight: (newHeight: number) => void;
  isAutoFitContent: boolean;
  toggleAutoFitContent: (value?: boolean) => void;
  isAutoHeight: boolean;
  beforeAutoFitMouseDown: () => void;

  sectionStyles: Models.MasterScreen.SectionStylesMap;
  setBrandStyle: (style: Models.BrandStyleMap) => void;
  hasBrandStyleHeadingTag: boolean;
};

const EditorToolbar: React.FunctionComponent<Props> = (props) => {
  const {
    layoutId,
    projectType,
    returnFocusToEditor,
    isAutoHeight,
    editorProps,
    editorSetters,
    contentMinHeight,
    styles,
    stylesSetters,
    sectionStyles,
    setBrandStyle,
    cellProps,
    toggleCellHeight,
    toggleColumnWidth,
    isAutoFitContent,
    toggleAutoFitContent,
    beforeAutoFitMouseDown,
    hasBrandStyleHeadingTag,
  } = props;

  const { allowBackgroundOpacity, allowBackgroundImage } = Constants.ProjectsConfig[projectType];
  const [activeDropdown, setActiveDropdown] = useState<ToolbarDropdownType | null>(null);
  const wrapperOnMouseDownHackCallback = (event: React.MouseEvent<HTMLDivElement>, dropDownType: ToolbarDropdownType): void => {
    event.preventDefault();
    setActiveDropdown(activeDropdown !== dropDownType ? dropDownType : null);
  };

  const closeDialog = (_: MouseEvent, type, isTarget): void => {
    const isStyleDropdownOpen = activeDropdown === type;

    if (!isTarget && isStyleDropdownOpen) {
      setActiveDropdown(null);
    }
  };

  return (
    <BaseToolbar layoutId={layoutId} dataTestId='editorToolbar-baseToolbar'>
      <Subtitle title={intlGet('EditorToolbar.Asset', 'Cell')} dataTestId="editorToolbar-subtitle-cell" />
      <ControlGroup dataTestId='controlGroup0'>
        <HeightWidth
          isAutoHeight={isAutoHeight}
          isAutoFitContent={isAutoFitContent}
          height={cellProps.cellHeight}
          maxHeight={cellProps.maxCellHeight}
          minHeight={contentMinHeight}
          toggleHeight={toggleCellHeight}
          disableCellWidthEditing={cellProps.disableCellWidthEditing}
          isIncreasingWidthDisabled={cellProps.isIncreasingWidthDisabled}
          minWidth={Constants.MinCellWidth.TEXT}
          width={cellProps.cellWidth}
          toggleColumnWidth={toggleColumnWidth}
          toggleAutoFitContent={toggleAutoFitContent}
          dataTestId='editorToolbar-heightWidth'
        />
        <AutoFitContentToggle
          isAutoFitContent={isAutoFitContent}
          isAutoHeight={isAutoHeight}
          toggleAutoFitContent={toggleAutoFitContent}
          beforeMouseDown={beforeAutoFitMouseDown}
          dataTestId='editorToolbar-autoFit'
        />
      </ControlGroup>
      <Subtitle title={intlGet('EditorToolbar.Asset', 'Text')} dataTestId="toolbar-subtitle-text"/>
      <ControlGroup dataTestId='controlGroup1'>
        <BrandStyleControl
          activeDropdown={activeDropdown}
          returnFocusToEditor={returnFocusToEditor}
          brandStyle={styles.brandStyle}
          closeDialog={closeDialog}
          brandStyleChanged={Boolean(styles.brandStyleChanged)}
          setStyle={setBrandStyle}
          sectionStyles={sectionStyles}
          wrapperOnMouseDownHackCallback={wrapperOnMouseDownHackCallback}
          dataTestId='editorToolbar-brandStyleControl'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup2'>
        <FontFamilyControl
          returnFocusToEditor={returnFocusToEditor}
          activeDropdown={activeDropdown}
          wrapperOnMouseDownHackCallback={wrapperOnMouseDownHackCallback}
          closeDialog={closeDialog}
          setFontFamily={(font, charStyle): void => {
            editorSetters.fontFamily(font, charStyle);
            returnFocusToEditor();
          }}
          activeFont={editorProps.fontFamily}
          dataTestId='editorToolbar-fontFamilyControl'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup3'>
        <FontSize
          fontSize={editorProps.fontSize}
          toggleFontSize={(size): void => {
            editorSetters.fontSize(size);
            returnFocusToEditor();
          }}
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='editorToolbar-fontSizeControl'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup8'>
        <HeadingTagControl
          hasBrandStyleHeadingTag={hasBrandStyleHeadingTag}
          currentHeadingTag={editorProps.headingTag}
          toggleHeadingTag={editorSetters.headingTag}
          dataTestId='editorToolbar-headingTagControl'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup4'>
        <CharStyleControl
          activeStyles={editorProps.charStyle}
          onToggle={(value): void => {
            editorSetters.charStyle(value);
            returnFocusToEditor();
          }}
          dataTestId='editorToolbar-charStyleControl'
        />
        <ScriptControl
          type={editorProps.scriptStyle}
          onToggle={(value): void => {
            editorSetters.scriptStyle(value);
            returnFocusToEditor();
          }}
          dataTestId='editorToolbar-scriptControl'
        />
        <FontColor
          color={editorProps.fontColor}
          toggleFontColor={(value): void => {
            editorSetters.fontColor(value);
            returnFocusToEditor();
          }}
          defaultColor={Constants.DefaultCustomStyle.FONT_COLOR as string}
          dataTestId='editorToolbar-fontColor'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup5'>
        <TextHorizontalAlignmentControls
          blockType={editorProps.blockType}
          wrapperOnMouseDownHackCallback={wrapperOnMouseDownHackCallback}
          activeDropdown={activeDropdown}
          closeDialog={closeDialog}
          onToggle={(value): void => {
            editorSetters.blockType(value);
            returnFocusToEditor();
          }}
          dataTestId='editorToolbar-textHorizontalAlignment'
        />
        <TextVerticalAlignment
          alignment={styles.verticalAlignment}
          toggleAlignment={stylesSetters.verticalAlignment}
          dataTestId='editorToolbar-textVerticalAlignment'
        />
        <TextLineHeight
          blockLineHeight={editorProps.blockLineHeight}
          setLineHeight={editorSetters.blockLineHeight}
          dataTestId='editorToolbar-textLineHeight'
        />
        <BulletedListControl
          active={editorProps.blockType === Constants.TextHorizontalAlignmentType.UNORDERED_LIST}
          color={editorProps.bulletColor}
          defaultColor={Constants.DefaultCustomStyle.FONT_COLOR as string}
          onToggle={(): void => {
            editorSetters.blockType(Constants.TextHorizontalAlignmentType.UNORDERED_LIST);
            returnFocusToEditor();
          }}
          toggleBulletColor={(value): void => {
            editorSetters.bulletColor(value);
            returnFocusToEditor();
          }}
          dataTestId='editorToolbar-bulletedListControl'
        />
        <WrapControl
          isNoWrapActive={editorProps.textNoWrap}
          onToggle={editorSetters.textNoWrap}
          dataTestId='editorToolbar-wrapControl'
        />
      </ControlGroup>

      <ControlGroup dataTestId='controlGroup6'>
        <Link
          isDisabled={!editorProps.linkApplicable}
          link={editorProps.link ?? ''}
          toggleLink={editorSetters.link}
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='editorToolbar-link'
        />
        <TextAbbreviationControl
          selectedText={editorProps.abbreviationTerm}
          abbreviationId={editorProps.abbreviationId}
          onChange={editorSetters.abbreviationId}
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='editorToolbar-textAbbreviation'
        />
        <Padding
          padding={styles.padding}
          togglePadding={stylesSetters.padding}
          className='mr-0'
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='editorToolbar-padding'
        />
        <Token
          className='mr-0'
          addToken={editorSetters.projectMetadataToken}
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='editorToolbar-token'
        />
      </ControlGroup>
      <ControlGroup dataTestId='controlGroup7'>
        <BorderControl
          border={styles.border}
          toggleBorder={stylesSetters.border}
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='editorToolbar-borderControl'
        />
        <FillColor
          activeColor={styles.backgroundColor}
          toggleColor={stylesSetters.backgroundColor}
          opacity={styles.backgroundColorOpacity}
          toggleOpacity={allowBackgroundOpacity ? stylesSetters.backgroundColorOpacity : undefined}
          gradient={styles.backgroundGradient}
          toggleGradient={stylesSetters.backgroundGradient}
          returnFocusToEditor={returnFocusToEditor}
          dataTestId='editorToolbar-fillColor'
        />
        {allowBackgroundImage &&
          <BackgroundImage
            backgroundImage={styles.backgroundImage}
            toggleBackgroundImage={stylesSetters.backgroundImage}
            returnFocusToEditor={returnFocusToEditor}
            dataTestId='editorToolbar-backgroundImage'
          />
        }
        <BorderRadius
          borderRadius={styles.borderRadius}
          title={HOVER_HINT.CELL}
          dropdownWarningMessage={intlGet('EditorToolbar.Tooltip', 'WarningMessage')}
          toggleBorderRadius={stylesSetters.borderRadius}
          dropdownWarningIconClassName={css.lastDropdownWarningIcon}
          dataTestId='editorToolbar-borderRadius'
        />
      </ControlGroup>
    </BaseToolbar>
  );
};

export default EditorToolbar;
